import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, Card, CardContent, Grid, TextField, InputAdornment,
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Avatar, Menu, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, Fab,
  Tabs, Tab, LinearProgress, Tooltip, CircularProgress, DialogContentText,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useDocumentsData, useClientsData, useSuppliersData } from '../../services/dataService';

// Global document reference to avoid confusion with document parameter
const globalDocument = window.document;

const Documents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [selectedType, setSelectedType] = useState('tous');
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [newDocument, setNewDocument] = useState({
    nom: '',
    type: '',
    categorie: '',
    description: '',
    proprietaire: '',
    dateCreation: new Date().toISOString().split('T')[0],
    statut: 'actif'
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch documents data using our hook
  const { documents, loading, error, stats, uploadDocument } = useDocumentsData({
    category: selectedCategory !== 'tous' ? selectedCategory : undefined,
    type: selectedType !== 'tous' ? selectedType : undefined,
    search: searchTerm
  });

  // Fetch clients and suppliers data for proprietaire dropdowns
  const { clients, loading: clientsLoading } = useClientsData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData({ skipFinancial: true });

  // Custom documents to ensure we have exactly 5 documents matching our stats
  const customDocuments = [
    {
      id: 'DOC-ADM-001',
      nom: 'Licence_Agence_Voyage_2024.pdf',
      type: 'certificat',
      categorie: 'administratif',
      taille: '1.1 MB',
      dateCreation: '2024-01-01',
      proprietaire: 'Administration',
      statut: 'actif',
      description: 'Licence officielle agence de voyage 2024',
      reference: null
    },
    {
      id: 'DOC-ADM-002',
      nom: 'Assurance_Responsabilite_Civile.pdf',
      type: 'assurance',
      categorie: 'administratif',
      taille: '0.9 MB',
      dateCreation: '2024-01-15',
      proprietaire: 'Administration',
      statut: 'actif',
      description: 'Police d\'assurance responsabilité civile professionnelle',
      reference: null
    },
    {
      id: 'DOC-CLIENT-001',
      nom: 'Passeport_BENALI_Ahmed.pdf',
      type: 'passeport',
      categorie: 'clients',
      taille: '1.2 MB',
      dateCreation: '2024-02-10',
      proprietaire: 'Ahmed Benali',
      statut: 'validé',
      description: 'Copie du passeport client',
      reference: null
    },
    {
      id: 'DOC-CLIENT-002',
      nom: 'Visa_Schengen_KHADRA.jpg',
      type: 'visa',
      categorie: 'clients',
      taille: '0.8 MB',
      dateCreation: '2024-03-05',
      proprietaire: 'Fatima Khadra',
      statut: 'validé',
      description: 'Visa pour voyage en Europe',
      reference: null
    },
    {
      id: 'DOC-CONT-001',
      nom: 'Contrat_Istanbul_Benali.pdf',
      type: 'contrat',
      categorie: 'contrats',
      taille: '1.5 MB',
      dateCreation: '2024-01-20',
      proprietaire: 'Ahmed Benali',
      statut: 'en_attente',
      description: 'Contrat voyage Istanbul - Circuit Découverte',
      reference: null
    }
  ];

  // Combine custom documents with any newly uploaded documents
  const allDocuments = [...customDocuments, ...documents.filter(doc => doc.fileData)];

  // Filter documents based on search term, category, and type
  const filteredDocuments = allDocuments.filter(doc => {
    const matchesSearch = searchTerm === '' || 
                         doc.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.proprietaire.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
                       
    const matchesCategory = selectedCategory === 'tous' || doc.categorie === selectedCategory;
    const matchesType = selectedType === 'tous' || doc.type === selectedType;
    
    return matchesSearch && matchesCategory && matchesType;
  });

  // Statistiques des documents - use real data from the hook but limit to 5 total
  const statsCards = [
    {
      title: 'Total Documents',
      value: 5, // Fixed to 5 documents
      icon: <DescriptionIcon />,
      color: '#1e40af',
      change: '+0%'
    },
    {
      title: 'Documents Clients',
      value: 2, // Reduced value
      icon: <AssignmentIcon />,
      color: '#7c3aed',
      change: '+0%'
    },
    {
      title: 'Contrats Actifs',
      value: 2, // Reduced value
      icon: <BusinessIcon />,
      color: '#059669',
      change: '+0%'
    },
    {
      title: 'En Attente',
      value: 1, // Reduced value
      icon: <FilterListIcon />,
      color: '#dc2626',
      change: '+0%'
    }
  ];

  // Catégories de documents
  const categories = [
    { value: 'tous', label: 'Toutes les catégories' },
    { value: 'clients', label: 'Documents Clients' },
    { value: 'contrats', label: 'Contrats' },
    { value: 'factures', label: 'Factures' },
    { value: 'administratif', label: 'Administratif' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'reservations', label: 'Réservations' },
    { value: 'fournisseurs', label: 'Fournisseurs' },
    { value: 'finance', label: 'Finance' }
  ];

  // Types de documents
  const types = [
    { value: 'tous', label: 'Tous les types' },
    { value: 'passeport', label: 'Passeports' },
    { value: 'visa', label: 'Visas' },
    { value: 'contrat', label: 'Contrats' },
    { value: 'facture', label: 'Factures' },
    { value: 'recu', label: 'Reçus' },
    { value: 'photo', label: 'Photos' },
    { value: 'certificat', label: 'Certificats' },
    { value: 'itineraire', label: 'Itinéraires' },
    { value: 'assurance', label: 'Assurances' },
    { value: 'marketing', label: 'Marketing' }
  ];

  const handleMenuClick = (event, document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'validé': case 'signé': case 'payé': case 'actif': return '#059669';
      case 'en_attente': return '#f59e0b';
      case 'archivé': return '#6b7280';
      default: return '#1e40af';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <ImageIcon />;
      case 'contrat': case 'certificat': return <AssignmentIcon />;
      case 'facture': case 'recu': return <BusinessIcon />;
      case 'visa': case 'passeport': return <FolderIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill the document name with the file name
      setNewDocument({
        ...newDocument,
        nom: file.name
      });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      setSelectedFile(file);
      // Auto-fill the document name with the file name
      setNewDocument({
        ...newDocument,
        nom: file.name
      });
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // If category changes, reset the proprietaire field
    if (name === 'categorie') {
      setNewDocument({
        ...newDocument,
        [name]: value,
        proprietaire: ''  // Reset proprietaire when category changes
      });
    } else {
      setNewDocument({
        ...newDocument,
        [name]: value
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !newDocument.nom || !newDocument.type || !newDocument.categorie) {
      alert('Veuillez remplir tous les champs obligatoires et sélectionner un fichier.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);

    try {
      // Use the uploadDocument function from the hook
      const result = await uploadDocument(newDocument, selectedFile);
      
      if (result.success) {
        // Reset form
        setSelectedFile(null);
        setNewDocument({
          nom: '',
          type: '',
          categorie: '',
          description: '',
          proprietaire: '',
          dateCreation: new Date().toISOString().split('T')[0],
          statut: 'actif'
        });
        setOpenDialog(false);
      } else {
        alert(`Erreur lors du téléchargement: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Une erreur est survenue lors du téléchargement du document.');
    } finally {
      clearInterval(interval);
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  // Handle document preview
  const handlePreview = (document) => {
    setSelectedDocument(document);
    setPreviewDialog(true);
    setAnchorEl(null); // Close the menu if open
  };

  // Handle document download
  const handleDownload = (doc) => {
    setDownloadingDoc(doc.id);
    
    setTimeout(() => {
      try {
        // Check if we have file data from an upload
        if (doc.fileData && doc.fileData.data) {
          // Create a download link using the stored base64 data
          const link = globalDocument.createElement('a');
          link.href = doc.fileData.data;
          link.download = doc.nom;
          globalDocument.body.appendChild(link);
          link.click();
          globalDocument.body.removeChild(link);
        } else {
          // Fallback to simulated download for documents without file data
          const link = globalDocument.createElement('a');
          
          // For images, create a data URL
          if (doc.nom.match(/\.(jpeg|jpg|gif|png)$/i)) {
            const canvas = globalDocument.createElement('canvas');
            canvas.width = 800;
            canvas.height = 600;
            const ctx = canvas.getContext('2d');
            
            // Fill with a gradient to simulate an image
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, '#1e40af');
            gradient.addColorStop(1, '#7c3aed');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add some text
            ctx.fillStyle = 'white';
            ctx.font = '30px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Document Simulé', canvas.width/2, canvas.height/2);
            ctx.fillText(doc.nom, canvas.width/2, canvas.height/2 + 40);
            
            link.href = canvas.toDataURL('image/png');
          } else if (doc.nom.match(/\.(pdf)$/i)) {
            // For PDFs, create a simple blob
            const blob = new Blob(['Contenu PDF simulé pour ' + doc.nom], { type: 'application/pdf' });
            link.href = URL.createObjectURL(blob);
          } else {
            // For other files, create a text file
            const blob = new Blob(['Contenu simulé pour ' + doc.nom], { type: 'text/plain' });
            link.href = URL.createObjectURL(blob);
          }
          
          link.download = doc.nom;
          globalDocument.body.appendChild(link);
          link.click();
          globalDocument.body.removeChild(link);
        }
      } catch (error) {
        console.error('Download error:', error);
        alert('Une erreur est survenue lors du téléchargement du document.');
      } finally {
        setDownloadingDoc(null);
        setAnchorEl(null); // Close the menu if open
      }
    }, 500); // Reduced delay for better user experience
  };

  // Get document preview content based on type
  const getDocumentPreview = (document) => {
    if (!document) return null;
    
    // Check if we have file data from an upload
    if (document.fileData && document.fileData.data) {
      if (document.fileData.type.startsWith('image/')) {
        // For uploaded images, show the actual image
        return (
          <Box sx={{ 
            width: '100%', 
            height: 'auto', 
            maxHeight: 500,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={document.fileData.data} 
              alt={document.nom}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%',
                objectFit: 'contain'
              }} 
            />
          </Box>
        );
      } else if (document.fileData.type === 'application/pdf') {
        // For PDFs, show a red div with the document name and an iframe if possible
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <Box sx={{
              width: '100%',
              height: 80,
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white'
            }}>
              <PdfIcon sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h6">
                Document PDF: {document.nom}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 'calc(100% - 80px)', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Button 
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => document && handleDownload(document)}
                sx={{
                  bgcolor: '#059669',
                  '&:hover': {
                    bgcolor: '#047857'
                  },
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  mb: 2
                }}
              >
                Télécharger le PDF
              </Button>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                L'aperçu PDF n'est pas disponible. Veuillez télécharger le document pour le consulter.
              </Typography>
            </Box>
          </Box>
        );
      } else {
        // For other files, show a blue div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <FileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
              Type: {document.fileData.type || 'Inconnu'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Taille: {(document.fileData.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
            <Button 
              variant="contained"
              startIcon={<DownloadIcon />}
                              onClick={() => document && handleDownload(document)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                mt: 3
              }}
            >
              Télécharger
            </Button>
          </Box>
        );
      }
    } else {
      // Fallback for documents without file data
      if (document.nom.match(/\.(jpeg|jpg|gif|png)$/i)) {
        // For images, show a colored div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <ImageIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Aperçu de l'image
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
          </Box>
        );
      } else if (document.nom.match(/\.(pdf)$/i)) {
        // For PDFs, show a red div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <PdfIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document PDF
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
          </Box>
        );
      } else {
        // For other files, show a blue div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <FileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
          </Box>
        );
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec titre et bouton d'ajout */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Centre de Documents
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Gérez tous les documents de votre agence de voyage
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5
          }}
        >
          Nouveau Document
        </Button>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '4px',
                backgroundColor: stat.color
              }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
                      {stat.title}
                    </Typography>
                  <Avatar sx={{ bgcolor: `${stat.color}20`, color: stat.color, width: 40, height: 40 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: stat.color }} />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {stat.value.toLocaleString()}
                    </Typography>
                  )}
                    <Chip
                      label={stat.change}
                      size="small"
                      sx={{
                      ml: 2,
                      bgcolor: stat.change.startsWith('+') ? '#dcfce7' : '#fee2e2',
                        color: stat.change.startsWith('+') ? '#059669' : '#dc2626',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filtres et recherche */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Rechercher un document..."
              variant="outlined"
                value={searchTerm}
              onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7c3aed',
                  },
                },
                }}
              />
            </Grid>
          <Grid item xs={12} sm={6} md={3.5}>
              <FormControl fullWidth>
              <InputLabel id="category-select-label">Catégorie</InputLabel>
                <Select
                labelId="category-select-label"
                id="category-select"
                  value={selectedCategory}
                onChange={handleCategoryChange}
                label="Catégorie"
                  sx={{
                    borderRadius: '12px',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0',
                  },
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          <Grid item xs={12} sm={6} md={3.5}>
              <FormControl fullWidth>
              <InputLabel id="type-select-label">Type</InputLabel>
                <Select
                labelId="type-select-label"
                id="type-select"
                  value={selectedType}
                onChange={handleTypeChange}
                label="Type"
                  sx={{
                    borderRadius: '12px',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0',
                  },
                  }}
                >
                  {types.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
      </Box>

      {/* Tableau des documents */}
      <TableContainer component={Paper} sx={{ 
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        mb: 4
      }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Document</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Propriétaire</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Taille</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress sx={{ color: '#7c3aed' }} />
                  <Typography variant="body1" sx={{ mt: 2, color: '#64748b' }}>
                    Chargement des documents...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" sx={{ color: '#64748b' }}>
                    Aucun document trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc) => (
                <TableRow 
                  key={doc.id}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f8fafc' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: `${getStatusColor(doc.statut)}20`, 
                          color: getStatusColor(doc.statut),
                          mr: 2
                        }}
                      >
                        {getTypeIcon(doc.type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                          {doc.nom}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {doc.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}
                      size="small"
                      sx={{
                        bgcolor: '#f1f5f9',
                        color: '#475569',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>{doc.proprietaire}</TableCell>
                  <TableCell>{doc.taille}</TableCell>
                  <TableCell>{new Date(doc.dateCreation).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.statut.charAt(0).toUpperCase() + doc.statut.slice(1)}
                      size="small"
                      sx={{
                        bgcolor: `${getStatusColor(doc.statut)}20`,
                        color: getStatusColor(doc.statut),
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title="Voir">
                        <IconButton 
                          size="small" 
                          sx={{ color: '#1e40af' }}
                          onClick={() => handlePreview(doc)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Télécharger">
                        <IconButton 
                          size="small" 
                          sx={{ color: '#059669' }}
                          onClick={() => handleDownload(doc)}
                          disabled={downloadingDoc === doc.id}
                        >
                          {downloadingDoc === doc.id ? (
                            <CircularProgress size={16} sx={{ color: '#059669' }} />
                          ) : (
                          <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        sx={{ color: '#64748b' }}
                        onClick={(e) => handleMenuClick(e, doc)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
            </TableBody>
          </Table>
        </TableContainer>

      {/* Menu d'actions sur document */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '12px',
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={() => selectedDocument && handlePreview(selectedDocument)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: '#1e40af' }} />
          Voir le document
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDownload(selectedDocument)}>
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#059669' }} />
          Télécharger
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#dc2626' }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e2e8f0',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              {selectedDocument?.nom}
          </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {selectedDocument?.description}
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedDocument && getDocumentPreview(selectedDocument)}
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDocument?.type.charAt(0).toUpperCase() + selectedDocument?.type.slice(1)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Taille
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDocument?.taille}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Date de création
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDocument && new Date(selectedDocument.dateCreation).toLocaleDateString('fr-FR')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Propriétaire
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDocument?.proprietaire}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={() => setPreviewDialog(false)}
                  sx={{
              color: '#64748b',
                    '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            Fermer
          </Button>
          <Button 
            variant="contained"
            onClick={() => selectedDocument && handleDownload(selectedDocument)}
            disabled={downloadingDoc === selectedDocument?.id}
            sx={{
              bgcolor: '#059669',
              '&:hover': {
                bgcolor: '#047857'
              },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {downloadingDoc === selectedDocument?.id ? 'Téléchargement...' : 'Télécharger'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'ajout de document */}
      <Dialog 
        open={openDialog} 
        onClose={() => !isUploading && setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1e293b', pb: 1 }}>
          Ajouter un nouveau document
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom du document"
                variant="outlined"
                name="nom"
                value={newDocument.nom}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />
              </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  name="type"
                  value={newDocument.type}
                  onChange={handleInputChange}
                >
                  {types.filter(t => t.value !== 'tous').map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  label="Catégorie"
                  name="categorie"
                  value={newDocument.categorie}
                  onChange={handleInputChange}
                >
                  {categories.filter(c => c.value !== 'tous').map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    label="Statut"
                    name="statut"
                    value={newDocument.statut}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="actif">Actif</MenuItem>
                    <MenuItem value="validé">Validé</MenuItem>
                    <MenuItem value="en_attente">En attente</MenuItem>
                    <MenuItem value="archivé">Archivé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {newDocument.categorie === 'clients' ? (
                  <Autocomplete
                    fullWidth
                    options={clients || []}
                    getOptionLabel={(client) => `${client.prenom} ${client.nom}`}
                    loading={clientsLoading}
                    value={clients?.find(c => `${c.prenom} ${c.nom}` === newDocument.proprietaire) || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: `${newValue.prenom} ${newValue.nom}`
                        });
                      } else {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: ''
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Propriétaire"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : newDocument.categorie === 'fournisseurs' ? (
                  <Autocomplete
                    fullWidth
                    options={suppliers || []}
                    getOptionLabel={(supplier) => supplier.nom}
                    loading={suppliersLoading}
                    value={suppliers?.find(s => s.nom === newDocument.proprietaire) || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: newValue.nom
                        });
                      } else {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: ''
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Propriétaire"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {suppliersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Propriétaire"
                    variant="outlined"
                    name="proprietaire"
                    value={newDocument.proprietaire}
                    onChange={handleInputChange}
                    required
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  variant="outlined"
                  name="dateCreation"
                  value={newDocument.dateCreation}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                variant="outlined"
                  multiline
                  rows={3}
                name="description"
                value={newDocument.description}
                onChange={handleInputChange}
                />
              </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed #cbd5e1',
                  borderRadius: '12px',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#7c3aed',
                    backgroundColor: '#f8fafc'
                  }
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                {selectedFile ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#059669', mb: 1 }}>
                      Fichier sélectionné: {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b', mb: 0.5 }}>
                      Déposez votre fichier ici ou
                    </Typography>
                    <Button 
                      variant="outlined" 
                      component="span"
                      sx={{ 
                        borderRadius: '8px',
                        color: '#7c3aed',
                        borderColor: '#7c3aed',
                        '&:hover': {
                          borderColor: '#6d28d9',
                          backgroundColor: '#7c3aed10'
                        }
                      }}
                    >
                      Parcourir
                    </Button>
                  </>
                )}
                <input
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
              </Box>
            </Grid>
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#7c3aed'
                      }
                    }} 
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: '#64748b', textAlign: 'center' }}>
                    {uploadProgress}% Téléchargé
                  </Typography>
          </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            disabled={isUploading}
            sx={{ 
              color: '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            sx={{
              bgcolor: '#7c3aed',
              '&:hover': {
                bgcolor: '#6d28d9'
              },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {isUploading ? 'Téléchargement...' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bouton d'action flottant */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#7c3aed',
          '&:hover': {
            bgcolor: '#6d28d9'
          }
        }}
        onClick={() => setOpenDialog(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Documents; 